// Times and ages on the hoops page, in the VIEWER'S zone. P6-328b (GN-1b).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THIS IS THE ONE PLACE ON THE SITE THAT RENDERS IN THE READER'S ZONE, ON
//    PURPOSE
// ═══════════════════════════════════════════════════════════════════════════
//
// `freshness.js` fixes UTC and says why: an as-of stamp is an INSTANT, and
// rendering it in the reader's zone gives six people six answers to "how old is
// this". That rule is about a stamp's AGE, and this file keeps it — an age here is
// arithmetic on two instants and has no zone at all. What differs is a TIP TIME:
// *"Times are scheduled in ET and shown in the viewer's zone, with the zone named.
// When the local date differs from the ET slate date, the tip time gets a weekday
// in front (UK viewers see `Wed 3:00 AM`)."* (`HANDOFF.md`.) A person deciding when
// to be on the couch wants their own clock, and the zone is named wherever a time
// is, so nobody has to guess whose it is.
//
// ⚠️ `timeZone` IS A PARAMETER, `undefined` MEANING THE DEVICE'S. Production never
// passes one. It is there because a test that depends on the zone of the machine it
// runs on has a different truth value in CI (UTC) and on a laptop (Eastern), which
// is gotcha 23's two-timezones-four-hours-apart in a new place.
//
// ⚠️ `en-US` THROUGHOUT, as the design's prototype does: it names zones as `EDT`,
// `PST` and `GMT+1`, which a US-based crew reads at a glance. The day label is the
// design's `en-GB` (`Sat 3 Oct`), also as drawn.

const formatters = new Map()

function formatter(kind, timeZone, locale, options) {
  const key = `${kind}|${locale}|${timeZone ?? ''}`
  let found = formatters.get(key)
  if (!found) {
    found = new Intl.DateTimeFormat(locale, { ...options, ...(timeZone ? { timeZone } : {}) })
    formatters.set(key, found)
  }
  return found
}

/** How long ago, in the fewest characters that are still true: `14s`, `7m`, `3h`, `2d`. */
export function ageText(seconds) {
  const s = Math.max(0, Math.floor(seconds))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  if (s < 86_400) return `${Math.floor(s / 3600)}h`
  return `${Math.floor(s / 86_400)}d`
}

/**
 * Whole seconds from a stamp to now, never negative.
 *
 * A stamp from the FUTURE (the bot's clock a moment ahead of this one) is `0`, not
 * a negative age: "0s" is what a fresh fetch reads like, and a minus sign on a
 * freshness label would be the page reporting a clock disagreement nobody can act on.
 */
export function ageSeconds(stampMs, nowMs) {
  return Math.max(0, Math.round((nowMs - stampMs) / 1000))
}

/** `7:00 PM`, in the viewer's zone. */
export function clockTime(ms, timeZone) {
  return formatter('clock', timeZone, 'en-US', { hour: 'numeric', minute: '2-digit' }).format(ms)
}

/** The zone's short name at that instant: `EDT`, `PST`, `GMT+1`. Named for the INSTANT, not for now. */
export function zoneName(ms, timeZone) {
  const parts = formatter('zone', timeZone, 'en-US', { timeZoneName: 'short' }).formatToParts(ms)
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
}

/** The viewer's calendar date at that instant, `YYYY-MM-DD`. */
export function localDate(ms, timeZone) {
  return formatter('date', timeZone, 'en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(ms)
}

/** `Sat`, in the viewer's zone. */
function weekdayShort(ms, timeZone) {
  return formatter('weekday', timeZone, 'en-US', { weekday: 'short' }).format(ms)
}

/**
 * A tip time for the viewer: `7:00 PM`, or `Sun 3:00 AM` when their date is not the slate's.
 *
 * `slateDate` is the ET date of the board (`2026-10-03`). A viewer east of Greenwich, or
 * anyone for a tip after midnight Eastern, sees a different calendar day from the one the
 * slate is named for, and a bare `3:00 AM` would read as "tonight" for a game that is
 * tomorrow morning for them. Without a slate date there is nothing to compare, so no
 * prefix — never a guess.
 */
export function tipTime(ms, slateDate, timeZone) {
  const time = clockTime(ms, timeZone)
  if (typeof slateDate !== 'string' || slateDate === '') return time
  return localDate(ms, timeZone) === slateDate ? time : `${weekdayShort(ms, timeZone)} ${time}`
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** `Sat 3 Oct` from `2026-10-03`, or `null` for anything that is not a date. */
export function dayLabel(isoDate) {
  const match = typeof isoDate === 'string' ? ISO_DATE.exec(isoDate) : null
  if (match === null) return null
  // Noon UTC, so no zone in the world can move the day: the label names the DATE.
  const when = new Date(`${isoDate}T12:00:00Z`)
  if (Number.isNaN(when.getTime())) return null
  // 🔴 READ THE RESULT BACK. V8 rolls `2026-02-31` over to the 3rd of March rather than refusing it
  // (`freshness.js::instantOf` records the same trap), so an impossible date would print as a plausible
  // day nobody can trace. A date is real only if it survives the round trip.
  if (when.toISOString().slice(0, 10) !== isoDate) return null
  return formatter('day', 'UTC', 'en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    .format(when)
    .replace(',', '')
}
